/**
 * TrueAnnualCostBar — a single stacked-bar visualisation showing all
 * four annual costs (tuition · mandatory fees · accommodation · food)
 * side-by-side so the "$0 tuition in Germany doesn't mean free"
 * story becomes visible in one glance.
 *
 * Rendered inside ResultRow's expanded narrative panel. Compact by
 * default — one 8-pixel-tall bar with a legend underneath. Segments
 * scale proportionally against `scaleMax` (the largest total across
 * the currently-visible rows), so unis are comparable at a glance.
 */
import { useMemo } from "react";
import { useCurrency } from "@/context/CurrencyContext";
import { trueCostTuitionNote } from "@/lib/feeYear";

const SEGMENTS = [
  { key: "tuition_usd",       label: "International tuition", cls: "bg-indigo-700" },
  { key: "fees_usd",          label: "Mandatory fees", cls: "bg-amber-500" },
  { key: "accommodation_usd", label: "Accommodation", cls: "bg-emerald-600" },
  { key: "food_usd",          label: "Food",         cls: "bg-rose-400" },
];

export default function TrueAnnualCostBar({ trueCost, scaleMax, testid }) {
  const { format } = useCurrency();
  const total = useMemo(() => {
    if (!trueCost) return 0;
    return SEGMENTS.reduce((acc, s) => acc + (Number(trueCost[s.key]) || 0), 0);
  }, [trueCost]);
  if (!trueCost || total === 0) return null;
  // Tuition missing (null) is unknown, not free: show only the known parts, labelled as such.
  const tuitionUnknown = trueCost.tuition_usd == null;
  const feesUnknown = !tuitionUnknown && trueCost.complete === false;
  const extrasUnverified = feesUnknown && trueCost.incomplete_reason === "Additional costs not verified";
  // Bar fills to `total/scaleMax` of its parent width; that lets the
  // caller compare rows fairly (all rows share one scaleMax).
  const barPct = scaleMax > 0 ? Math.min(100, (total / scaleMax) * 100) : 100;
  return (
    <div data-testid={testid} className="mt-3">
      <div className="flex items-center justify-between text-[10px] font-underground-sc tracking-wider text-stone-500 mb-1">
        <span data-testid={`${testid}-label`}>{tuitionUnknown ? "Known costs · tuition not on file" : extrasUnverified ? "Known costs · additional costs not verified" : feesUnknown ? "Known costs · required fees not verified" : "True annual cost"}</span>
        <span className="text-stone-900" data-testid={`${testid}-total`}>
          {format(Math.round(total))}/yr{tuitionUnknown ? " + tuition" : extrasUnverified ? " + additional costs" : feesUnknown ? " + fees" : ""}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-stone-100 overflow-hidden relative">
        <div className="h-full flex" style={{ width: `${barPct}%` }}>
          {SEGMENTS.map((s) => {
            const v = Number(trueCost[s.key]) || 0;
            if (v <= 0) return null;
            const pct = (v / total) * 100;
            return (
              <div
                key={s.key}
                className={s.cls}
                title={`${s.label}: ${format(Math.round(v))}/yr`}
                style={{ width: `${pct}%` }}
                data-testid={`${testid}-seg-${s.key}`}
              />
            );
          })}
        </div>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[10px] font-mono text-stone-600">
        {SEGMENTS.map((s) => {
          const v = Number(trueCost[s.key]) || 0;
          if (v <= 0) return null;
          return (
            <span key={s.key} className="inline-flex items-center gap-1">
              <span className={`h-2 w-2 rounded-sm ${s.cls}`} />
              <span className="uppercase tracking-wider">{s.label}</span>
              <span className="text-stone-900">{format(Math.round(v))}</span>
            </span>
          );
        })}
        {trueCostTuitionNote(trueCost) && (
          <span className="basis-full text-stone-500" data-testid={`${testid}-tuition-year`}>
            {trueCostTuitionNote(trueCost)}
          </span>
        )}
        {tuitionUnknown && (
          <span className="inline-flex items-center gap-1 text-stone-500" data-testid={`${testid}-tuition-unknown`}>
            <span className="h-2 w-2 rounded-sm border border-dashed border-indigo-700" />
            <span className="uppercase tracking-wider">Tuition</span>
            <span>not on file</span>
          </span>
        )}
      </div>
    </div>
  );
}
